
export const WhatIsT12 = () => {
  return (
    <section id="what-is-t12" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
              <span className="text-teal-600 text-sm font-semibold">Understanding T12</span>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What is Trailing 12 (T12) in Real Estate?
            </h2>

            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                <strong>Trailing 12 (T12)</strong> refers to the financial performance of an income-producing
                property over the most recent 12‑month period. It's a comprehensive snapshot of actual income,
                expenses, and net operating income (NOI) based on historical data rather than projections or
                estimates.
              </p>

              <p>
                In apartment building transactions, T12 is the single most important financial document buyers
                and lenders review. It provides an accurate picture of how the property has actually performed,
                not how it might perform or should perform.
              </p>

              <p>
                The "trailing" aspect means the data rolls forward continuously. If today is June 15, 2024,
                your T12 would cover June 1, 2023 through May 31, 2024. Next month, it would shift to July 1,
                2023 through June 30, 2024.
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg mt-6">
                <h4 className="font-bold text-gray-900 mb-2">Key Components of T12:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    <strong>Gross Rental Income:</strong> All rent collected from tenants
                  </li>
                  <li>
                    <strong>Other Income:</strong> Parking, laundry, pet fees, late fees, etc.
                  </li>
                  <li>
                    <strong>Vacancy Loss:</strong> Income lost due to unoccupied units
                  </li>
                  <li>
                    <strong>Operating Expenses:</strong> All costs to operate the property
                  </li>
                  <li>
                    <strong>Net Operating Income (NOI):</strong> Income minus expenses
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/62d98dc8f479010e7ed85982eec51606.jpg"
                alt="Professional analyzing trailing 12 financial statements for apartment building"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">12 Months</div>
                  <div className="text-sm text-gray-600">Rolling Historical Data</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-8 border border-gray-200">
            <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-file-chart-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Actual Performance</h3>
            <p className="text-gray-700">
              T12 shows what actually happened, not projections or estimates. It's based on real rent
              collected, actual expenses paid, and verified occupancy rates over 12 full months.
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-8 border border-gray-200">
            <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-refresh-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Rolling Forward</h3>
            <p className="text-gray-700">
              The 12‑month window continuously moves forward. Each month, the oldest month drops off and the
              most recent month is added, keeping the data current and relevant.
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-8 border border-gray-200">
            <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Verified Data</h3>
            <p className="text-gray-700">
              Buyers verify T12 against bank statements, tax returns, and rent rolls. Any discrepancies raise
              red flags and can kill deals or reduce your sale price significantly.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
